import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";

const CustomerContext = createContext({
    customer: null,
    loading: true,
    login: async () => {},
    register: async () => {},
    logout: () => {},
    refresh: async () => {},
});

const TOKEN_KEY = "cc_customer_token";

export function CustomerProvider({ children }) {
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) { setCustomer(null); setLoading(false); return; }
        try {
            const r = await api.get("/customer/me", { headers: { Authorization: `Bearer ${token}` } });
            setCustomer(r.data);
        } catch {
            localStorage.removeItem(TOKEN_KEY);
            setCustomer(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback(async (email, password) => {
        const r = await api.post("/customer/login", { email, password });
        localStorage.setItem(TOKEN_KEY, r.data.token);
        setCustomer(r.data.customer);
        return r.data.customer;
    }, []);

    const register = useCallback(async (name, email, password) => {
        const r = await api.post("/customer/register", { name, email, password });
        localStorage.setItem(TOKEN_KEY, r.data.token);
        setCustomer(r.data.customer);
        return r.data;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setCustomer(null);
    }, []);

    return (
        <CustomerContext.Provider value={{ customer, loading, login, register, logout, refresh }}>
            {children}
        </CustomerContext.Provider>
    );
}

export const useCustomer = () => useContext(CustomerContext);

/**
 * Convenience header helper — mounts the Bearer token if the customer is logged in.
 * Usage: `api.get("/customer/orders", withAuth())`
 */
export function withAuth(extra = {}) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return extra;
    return { ...extra, headers: { ...(extra.headers || {}), Authorization: `Bearer ${token}` } };
}
